import { Component, effect, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { resolveCallMode, IncomingCallPayload, SoftphoneStateDto, UserInterface } from 'voxr-shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CallService } from '../../../services/call.service';
import { SocketService } from '../../../services/socket.service';
import { AppService } from '../../../services/app.service';
import { catchError, debounceTime, defer, EMPTY, finalize, Subject, switchMap, tap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { UserService } from '../../../services/user.service';
import { TransferAvailabilityComponent } from '../transfer-availability/transfer-availability.component';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from "@angular/material/tooltip";
import { SoftphoneService, SoftphoneState } from '../../../services/softphone.service';
import { TransferInfoComponent, TransferInfoViewModel } from '../transfer-info/transfer-info.component';

@Component({
  selector: 'app-connection-stats',
  standalone: true,
  imports: [
    CommonModule, ButtonModule,
    MatMenuModule,
    TransferAvailabilityComponent,
    MatTooltipModule,
    TransferInfoComponent,
],
  templateUrl: './connection-stats.component.html'
})
export class ConnectionStatsComponent {
  private callService = inject(CallService);
  private socketService = inject(SocketService);
  private app = inject(AppService);
  private userService = inject(UserService);
  private softphoneService = inject(SoftphoneService);

  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;

  incomingCall = signal<IncomingCallPayload | undefined>(undefined);
  rejecting: boolean = false;
  socketConnected = false;
  softphoneState = signal<SoftphoneState>('inactive');
  softphoneStateDto = signal<SoftphoneStateDto | undefined>(undefined);

  user = signal<UserInterface | undefined>(undefined);
  agentInfoUpdate$ = new Subject<void>();
  agentInfoLoading = signal(false);
  readonly incomingTransferInfo = signal<TransferInfoViewModel | undefined>(undefined);

  constructor() {
    this.callService.incomingCall$.pipe(takeUntilDestroyed()).subscribe(async payload => await this.onIncomingCall(payload));
    this.socketService.connected$.pipe(takeUntilDestroyed(), debounceTime(1000)).subscribe(connected => this.socketConnected = connected);
    this.userService.user$.pipe(takeUntilDestroyed()).subscribe(user => this.user.set(user
      ? { ...user, agentInfo: user.agentInfo ? structuredClone(user.agentInfo) : undefined } as UserInterface : undefined));
    this.userService.softphoneStateDto$.pipe(takeUntilDestroyed()).subscribe(state => this.softphoneStateDto.set(state));
    this.softphoneService.softphoneState$.pipe(takeUntilDestroyed()).subscribe(state => this.softphoneState.set(state));

    this.agentInfoUpdate$.pipe(
      takeUntilDestroyed(), debounceTime(500),
      switchMap(() => defer(() => {
        this.agentInfoLoading.set(true);
        return this.userService.setAgentInfo(this.user()!.agentInfo!).pipe(
          tap(() => this.app.message$.next({ severity: 'success', summary: 'Success', detail: 'Updated live transfer availability.' })),
          catchError(() => {
            this.app.message$.next({ severity: 'danger', summary: 'Error', detail: 'Failed to update live transfer availability.' });
            return EMPTY;
          }),
          finalize(() => {
            this.agentInfoLoading.set(false);
            const saved = this.userService._user;
            if (saved && saved._id === this.user()?._id) this.user.set({ ...saved, agentInfo: saved.agentInfo ? structuredClone(saved.agentInfo) : undefined } as UserInterface);
          }),
        );
      }))
    ).subscribe();

    effect(() => {
      const incomingCall = this.incomingCall();
      const liveTransfer = this.user()?.agentInfo?.liveTransfer;
      const softphoneState = this.softphoneStateDto();
      const shouldHideMenu = !!incomingCall
        && !!softphoneState?.enabled
        && liveTransfer?.targetPriority === 'soft-phone'
        && this.softphoneState() === 'ready';

      if (incomingCall && !shouldHideMenu) this.menuTrigger?.openMenu();
      else this.menuTrigger?.closeMenu();
    });
  }

  async onIncomingCall(payload: IncomingCallPayload) {
    switch (payload.type) {
      case 'transfer':
        this.incomingCall.set(payload);
        this.incomingTransferInfo.set({
          handoffId: `${payload.call?._id || ''}:${payload.transfer?._id || ''}`,
          handoff: payload.handoff,
          callMode: resolveCallMode(payload.call),
          leadName: [payload.call?.contactInfo?.firstName, payload.call?.contactInfo?.lastName].filter(Boolean).join(' ').trim() || undefined,
          leadPhone: payload.call?.flow === 'INBOUND' ? payload.call?.fromNumber : payload.call?.toNumber,
          leadState: payload.call?.contactInfo?.state || payload.call?.contactInfo?.phoneState,
          campaignName: payload.campaign?.name,
          summary: payload.call?.summary,
        });
        break;
    }
  }

  async rejectTransfer() {
    try {
      this.rejecting = true;
      await this.callService.rejectTransfer(this.incomingCall()!.call!._id!.toString(), this.incomingCall()!.transfer!._id!.toString());
      this.app.message$.next({ severity: 'info', detail: 'Transfer rejected.' });
    } finally {
      this.rejecting = false;
      this.incomingCall.set(undefined);
      this.incomingTransferInfo.set(undefined);
    }
  }

  clearIncomingCall() {
    this.incomingCall.set(undefined);
    this.incomingTransferInfo.set(undefined);
  }

  onAgentInfoChange(agentInfo: UserInterface['agentInfo']) {
    const currentUser = this.user();
    if (currentUser && agentInfo) this.user.set({ ...currentUser, agentInfo } as UserInterface);
    this.agentInfoUpdate$.next();
  }
}

